import { useCallback, useEffect, useRef } from "react";

function pickRecordingMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
}

export default function useSessionRecording() {
  const audioContextRef = useRef(null);
  const destinationRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const activeRef = useRef(false);
  const mimeTypeRef = useRef("");
  const startedAtRef = useRef(0);
  const connectedStreamIdsRef = useRef(new Set());
  const attachTimerRef = useRef(null);

  const cleanup = useCallback(() => {
    if (attachTimerRef.current) clearInterval(attachTimerRef.current);
    attachTimerRef.current = null;
    try {
      recorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop());
    } catch {}
    recorderRef.current = null;
    destinationRef.current = null;
    chunksRef.current = [];
    activeRef.current = false;
    startedAtRef.current = 0;
    connectedStreamIdsRef.current = new Set();
    try {
      audioContextRef.current?.close?.();
    } catch {}
    audioContextRef.current = null;
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const startRecording = useCallback(async ({
    localStream,
    remoteStream,
    getLocalStream,
    getRemoteStream,
  }) => {
    if (typeof MediaRecorder === "undefined") return false;
    if (!localStream && !remoteStream) return false;

    cleanup();

    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return false;

    const ctx = new Ctx();
    const destination = ctx.createMediaStreamDestination();
    audioContextRef.current = ctx;
    destinationRef.current = destination;

    const connectStream = (stream) => {
      if (!stream) return;
      const id = stream.id || `stream-${stream.getAudioTracks?.()[0]?.id || ""}`;
      if (connectedStreamIdsRef.current.has(id)) return;
      const hasAudio = stream.getAudioTracks?.().length > 0;
      if (!hasAudio) return;
      const source = ctx.createMediaStreamSource(stream);
      const gain = ctx.createGain();
      gain.gain.value = 1;
      source.connect(gain).connect(destination);
      connectedStreamIdsRef.current.add(id);
    };

    connectStream(localStream);
    connectStream(remoteStream);
    connectStream(getLocalStream?.());
    connectStream(getRemoteStream?.());

    attachTimerRef.current = setInterval(() => {
      if (!activeRef.current) return;
      connectStream(getLocalStream?.());
      connectStream(getRemoteStream?.());
    }, 500);

    const mimeType = pickRecordingMimeType();
    mimeTypeRef.current = mimeType || "audio/webm";
    const recorder = mimeType
      ? new MediaRecorder(destination.stream, { mimeType })
      : new MediaRecorder(destination.stream);
    recorderRef.current = recorder;
    chunksRef.current = [];
    activeRef.current = true;
    startedAtRef.current = Date.now();

    recorder.ondataavailable = (evt) => {
      if (evt?.data?.size > 0) {
        chunksRef.current.push(evt.data);
      }
    };

    recorder.start(1000);
    return true;
  }, [cleanup]);

  const stopRecording = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || !activeRef.current) return null;

    const stopped = new Promise((resolve) => {
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current || "audio/webm" });
        const fallbackDurationSeconds = startedAtRef.current
          ? Number(((Date.now() - startedAtRef.current) / 1000).toFixed(2))
          : null;
        resolve({
          blob,
          mimeType: blob.type || mimeTypeRef.current || "audio/webm",
          sizeBytes: blob.size,
          durationSeconds: fallbackDurationSeconds,
        });
      };
    });

    recorder.stop();
    const data = await stopped;
    cleanup();
    return data;
  }, [cleanup]);

  return {
    startRecording,
    stopRecording,
  };
}
